<template>
  <div class="app-container">
    <div class="app-inner" :class="{ 'qc-fail-background': qcFailState }">
    
      <!-- Combined Blade -->
      <div class="blade-container">
      <div class="input-content">
        <input
          ref="scanInputElement"
          id="scan-input"
          v-model="scanInput"
          type="text"
          class="form-control form-control-lg"
          placeholder="Enter code to scan"
          inputmode="none"
          @focus="preventKeyboard"
        />
        <p class="main-info-text" :class="{ 'main-info-text--error': mainInfoIsError }">{{ mainInfoText }}</p>
      </div>
      
      <!-- Garment Info (only when garment is scanned, not in QC fail or QC pass state) -->
      <GarmentBlade v-if="selectedGarment && !qcFailState && !qcPassState" :garment="selectedGarment" />

      <!-- QC Pass: slot assignment -->
      <div v-if="qcPassState" class="slot-assignment">
        <div v-if="qcPassState.onlyGarmentInTicket" class="new-assignment-heading">New Assignment & Only Garment</div>
        <div v-else-if="qcPassState.isNewAssignment" class="new-assignment-heading">New assignment: Scan the slot & then the white ticket</div>
        <div v-if="qcPassState.onlyGarmentInTicket" class="single-garment-instruction">
          Scan the white ticket & put the garment on the packing rail
        </div>
        <div v-else-if="qcPassState.completesTicket" class="completes-ticket-heading">This garment will complete the white ticket.</div>
        <div v-if="!qcPassState.onlyGarmentInTicket && !qcPassState.isNewAssignment" class="slot-instruction-bubble">Scan the white ticket of the following slot</div>
        <div v-if="!qcPassState.onlyGarmentInTicket" class="slot-box">
          <div class="slot-value">{{ qcPassState.slotLabel }}</div>
        </div>
        <div class="slot-info-row">
          <span class="slot-info-label">Order ID</span>
          <span class="slot-info-value">{{ qcPassState.garment.orderId }}</span>
        </div>
        <div class="slot-info-row">
          <span class="slot-info-label">Garment</span>
          <span class="slot-info-value">{{ qcPassState.garment.garmentType }}</span>
        </div>
        <p v-if="reprintFeedbackMessage" class="reprint-feedback-message">{{ reprintFeedbackMessage }}</p>
        <button
          v-if="qcPassState.expectedServiceTicket && qcPassState.garment?.orderId"
          type="button"
          class="reprint-ticket-btn"
          :disabled="isReprintingServiceTicket"
          @click="reprintCurrentServiceTicket"
        >
          {{ isReprintingServiceTicket ? 'Printing...' : 'Reprint ServiceTicket' }}
        </button>
      </div>
    </div>

    <!-- Garment Pieced Up success / Ticket complete (last garment) / Single garment in ticket -->
    <div v-if="piecedUpState && piecedUpState.singleGarmentTicketComplete" class="pieced-up-area">
      <p class="pieced-up-text">Garment pieced up and white ticket complete</p>
    </div>
    <div v-else-if="piecedUpState && !ticketCompleteState" class="pieced-up-area">
      <p class="pieced-up-text">Garment Pieced Up (Slot {{ piecedUpState.slotLabel }})</p>
    </div>
    <div v-if="ticketCompleteState" class="pieced-up-area">
      <template v-if="ticketCompleteState.cleared">
        <p class="pieced-up-text">Slot cleared ({{ ticketCompleteState.slotLabel }})</p>
      </template>
      <template v-else>
        <p class="pieced-up-text">Garment Pieced Up (Slot {{ ticketCompleteState.slotLabel }})</p>
        <div class="ticket-complete-message">
          <p class="ticket-complete-line">The ticket is now complete,</p>
          <p class="ticket-complete-line">please clear the slot</p>
        </div>
        <button type="button" class="clear-slot-btn" @click="clearSlot">Clear Slot</button>
      </template>
    </div>

    <!-- QC Fail state: yellow area with message at top and compact garment box -->
    <div v-if="qcFailState" class="qc-fail-area">
      <p class="qc-fail-message">QC Failed ({{ qcFailState.reason }})</p>
      <div class="qc-fail-inner">
        <GarmentBlade :garment="qcFailState.garment" compact />
      </div>
    </div>
    </div>

    <!-- QC Note modal -->
    <Teleport to="body">
      <Transition name="qc-note-modal">
        <div v-if="qcNoteModal" class="qc-note-overlay">
          <div class="qc-note-modal-box">
            <div class="qc-note-header">
              <h2 class="qc-note-title">QC Notes</h2>
            </div>
            <div class="qc-note-content" :class="{ 'qc-note-content--scrollable': qcNoteModal.notes.length > 1 }">
              <div
                v-for="(note, index) in qcNoteModal.notes"
                :key="index"
                class="qc-note-entry"
              >
                <div class="qc-note-entry-text">{{ note.text }}</div>
                <div v-if="note.user || note.timestamp" class="qc-note-entry-meta">
                  <span v-if="note.user">{{ note.user }}</span>
                  <span v-if="note.user && note.timestamp"> - </span>
                  <span v-if="note.timestamp">{{ note.timestamp }}</span>
                </div>
              </div>
            </div>
            <div class="qc-note-footer">
              <button type="button" class="qc-note-close-btn" @click="closeQcNoteModal">Close</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Stain Removed? modal -->
    <Teleport to="body">
      <Transition name="qc-note-modal">
        <div v-if="stainModal" class="stain-overlay">
          <div class="stain-modal-box">
            <div class="stain-header">
              <h2 class="stain-title">Stain Removed?</h2>
              <p class="stain-times-failed">Times Failed: {{ stainModal.garment.timesFailed }}</p>
            </div>
            <div v-if="stainModal.garment.stainDescriptions.length > 0" class="stain-descriptions">
              <p
                v-for="(description, index) in stainModal.garment.stainDescriptions"
                :key="`${index}-${description}`"
                class="stain-description"
              >
                {{ description }}
              </p>
            </div>
            <div v-if="stainModal.garment.stainLayouts.length > 0" class="stain-layouts-wrap">
              <div class="stain-layouts-grid">
                <div
                  v-for="layout in stainModal.garment.stainLayouts"
                  :key="layout.id"
                  class="stain-layout-card"
                >
                  <div class="stain-layout-canvas">
                    <img
                      :src="layout.imageUrl"
                      :alt="layout.name"
                      class="stain-modal-image"
                    />
                    <span
                      v-for="marker in layout.markers"
                      :key="marker.id"
                      class="stain-marker"
                      :style="getStainMarkerStyle(layout.id, marker)"
                    />
                  </div>
                  <div class="stain-layout-name">{{ layout.name }}</div>
                </div>
              </div>
            </div>
            <div v-else class="stain-layouts-empty">
              No stain layout available for this garment.
            </div>
            <div class="stain-actions">
              <button type="button" class="stain-btn stain-btn--yes" @click="answerStain(true)">Yes</button>
              <button type="button" class="stain-btn stain-btn--no" @click="answerStain(false)">No</button>
            </div>
            <button
              v-if="Number(stainModal.garment.timesFailed) > 0"
              type="button"
              class="stain-btn stain-btn--recleaned"
              @click="answerStainRecleaned"
            >
              No: Recleaned already
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Repairs confirmation modal -->
    <Teleport to="body">
      <Transition name="qc-note-modal">
        <div v-if="repairsModal" class="repairs-overlay">
          <div class="repairs-modal-box">
            <div class="repairs-header">
              <h2 class="repairs-title">Have the following repair(s) been done?</h2>
            </div>
            <div class="repairs-list">
              <div
                v-for="(repair, index) in repairsModal.repairs"
                :key="index"
                class="repair-item"
              >
                <!-- <div class="repair-category">{{ repair.category }}</div>-->
                <div class="repair-category">{{ repair.servicetype }}</div>
                <div class="repair-description">{{ repair.description }}</div>
                <div class="repair-actions">
                  <button
                    type="button"
                    class="repair-btn repair-btn--yes"
                    :disabled="repairsModal.answers[index] !== undefined"
                    @click="answerRepair(index, true)"
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    class="repair-btn repair-btn--no"
                    :disabled="repairsModal.answers[index] !== undefined"
                    @click="answerRepair(index, false)"
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Repeat QC fail confirmation modal -->
    <Teleport to="body">
      <Transition name="qc-note-modal">
        <div v-if="repeatQcFailModal" class="repeat-qc-fail-overlay">
          <div class="repeat-qc-fail-modal-box">
            <div class="repeat-qc-fail-header">
              <h2 class="repeat-qc-fail-title">Repeat QC Fail</h2>
            </div>
            <p class="repeat-qc-fail-message">
              This garment has failed QC {{ repeatQcFailModal.timesFailed }} times, proceed to piece up instead?
            </p>
            <div class="repeat-qc-fail-actions">
              <button type="button" class="repeat-qc-fail-btn repeat-qc-fail-btn--yes" @click="answerRepeatQcFail(true)">Yes</button>
              <button type="button" class="repeat-qc-fail-btn repeat-qc-fail-btn--no" @click="answerRepeatQcFail(false)">No</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="qc-note-modal">
        <div v-if="alreadyPiecedUpModal" class="repeat-qc-fail-overlay">
          <div class="repeat-qc-fail-modal-box">
            <div class="repeat-qc-fail-header">
              <h2 class="repeat-qc-fail-title">Already Pieced Up</h2>
            </div>
            <p class="repeat-qc-fail-message">
              <template v-if="alreadyPiecedUpModal.onlyGarmentInTicket">
                You pieced up this garment <span class="already-pieced-up-badge">{{ alreadyPiecedUpModal.relativeText }} ago</span>. As this is an only garment please check the packing rail for any incorrect garment.
              </template>
              <template v-else>
                You pieced up this garment <span class="already-pieced-up-badge">{{ alreadyPiecedUpModal.relativeText }} ago on Slot {{ alreadyPiecedUpModal.slotLabel }} </span> Please check the slot for any incorrect garment.
              </template>
              <span class="already-pieced-up-timestamp">({{ alreadyPiecedUpModal.absoluteText }})</span>
            </p>
            <div class="repeat-qc-fail-actions">
              <button type="button" class="repeat-qc-fail-btn repeat-qc-fail-btn--proceed" @click="answerAlreadyPiecedUp(true)">Proceed</button>
              <button type="button" class="repeat-qc-fail-btn repeat-qc-fail-btn--no" @click="answerAlreadyPiecedUp(false)">Cancel</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import OrderService from "@/services/OrderService";
import RFIDService from "@/services/RFIDService";
import ServiceTicketService from "@/services/ServiceTicketService";
import { printServiceTicketQpup } from '../rfid/printServiceTicketHelper';
import { formatDateEN, formatDateTimeEN, formatDateYMD } from '@/utils/dateUtils';
import GarmentBlade from './GarmentComponent.vue';
import { setSidebarTitle, resetSidebarTitle } from '@/components/sidebar/state'
import { onMounted, onBeforeUnmount } from 'vue';

const scanInput = ref('')
const mainInfoText = ref('Ready to scan garment')
const mainInfoIsError = ref(false)
const selectedGarment = ref(null)
const qcFailState = ref(null) // { garment, reason }
const qcPassState = ref(null) // { garment, slotId, slotLabel, expectedServiceTicket }
const piecedUpState = ref(null) // { slotLabel } or { singleGarmentTicketComplete: true }
const ticketCompleteState = ref(null) // { slotLabel, cleared }
const qcNoteModal = ref(null) // { notes } or null
const repairsModal = ref(null) // { garment, repairs, answers } or null
const stainModal = ref(null) // { garment } or null
const repeatQcFailModal = ref(null) // { garment, config, timesFailed } or null
const alreadyPiecedUpModal = ref(null) // { garment, piecedUpAt } or null
const pendingGarmentCaseQueue = ref([])
const isActive = ref(true)
const scanInputElement = ref(null)
const isReprintingServiceTicket = ref(false)
const reprintFeedbackMessage = ref('')

let reprintFeedbackTimer = null

const EPOS_API_BASE = String(process.env.VUE_APP_EPOS_API_BASE || '').replace(/\/$/, '')

onMounted(() => {
  isActive.value = true
  setSidebarTitle('Q-PUP') // custom title for this view
  nextTick(() => {
    scanInputElement.value?.focus()
  })
})

onBeforeUnmount(() => {
  isActive.value = false
  if (scanDebounceTimer) {
    clearTimeout(scanDebounceTimer)
    scanDebounceTimer = null
  }
  if (reprintFeedbackTimer) {
    clearTimeout(reprintFeedbackTimer)
    reprintFeedbackTimer = null
  }
  resetSidebarTitle() // back to MPOS
})

const normalizeSlotDetails = (response) => {
  const responseData = response?.data?.data || response?.data || response || {}
  const slot = responseData?.slot || null
  if (!slot) return null

  const rawSlotId = slot.slot_id || slot.id || ''
  const slotBarcode = String(slot.slot_barcode || slot.barcode || '').trim().toUpperCase()
  const slotLabel = String(slot.slot_label || slot.label || slot.name || rawSlotId || '').trim().toUpperCase()
  const slotId = slotBarcode || (slotLabel ? `PUR01${slotLabel}` : String(rawSlotId || '').trim().toUpperCase())
  const isNewAssignment = toBoolean(
    responseData?.isNewAssignment ??
    responseData?.is_new_assignment ??
    slot?.isNewAssignment ??
    slot?.is_new_assignment
  )
  const isOnlyGarment = toBoolean(
    responseData?.isOnlyGarment ??
    responseData?.is_only_garment ??
    slot?.isOnlyGarment ??
    slot?.is_only_garment
  )
  const willComplete = toBoolean(
    responseData?.willComplete ??
    responseData?.will_complete ??
    slot?.willComplete ??
    slot?.will_complete
  )

  if (!slotId && !slotLabel && !isOnlyGarment) return null

  return {
    slotId,
    slotLabel,
    isNewAssignment,
    isOnlyGarment,
    willComplete,
  }
}

const normalizeServiceTicketBarcode = (value) => {
  const normalizedValue = String(value || '').replace(/\s/g, '').trim()
  const match = normalizedValue.match(/^([sS][tT]\d+)\/([0-9a-fA-F]+)$/)

  if (!match) return normalizedValue

  return `${match[1].toUpperCase()}/${match[2].toLowerCase()}`
}

const debugServiceTicketId = (stage, serviceTicketId, extra = {}) => {
  console.log('[Q-PUP] serviceTicketId', {
    stage,
    serviceTicketId,
    ...extra,
  })
}

const normalizePieceUpResult = (response) => {
  const responseData = response?.data?.data || response?.data || response || {}

  return {
    ticketComplete: toBoolean(
      responseData?.ticketComplete ??
      responseData?.ticket_complete ??
      responseData?.serviceTicketComplete ??
      responseData?.service_ticket_complete ??
      responseData?.ticket?.ticketComplete ??
      responseData?.ticket?.ticket_complete
    ),
  }
}

// QC fail codes: QCS20–QCS24
const QC_FAIL_CODES = {
  QCS20: { reason: 'Cleaning', messageSuffix: 'Please add to the cleaning rail.', failType: 'cleaning' },
  QCS21: { reason: 'Pressing', messageSuffix: 'Please add to the pressing queue.', failType: 'pressing' },
  QCS22: { reason: 'Damage', messageSuffix: 'Please send for damage assessment.', failType: 'damage' },
  QCS23: { reason: 'Repair', messageSuffix: 'Please add to the repair queue.', failType: 'repair' },
  QCS24: { reason: 'Stain Check', messageSuffix: 'Please add to stain check.', failType: 'stain' },
}

const QC_RETURN_ONLY_CODE = /^QCS30$/i
//the time window to show the "already pieced up" warning modal (1 hour)
const ALREADY_PIECED_UP_WARNING_WINDOW_MS = 30 * 60 * 1000

const toBoolean = (value) => value === true || value === 1 || value === '1'

const formatRelativeDuration = (fromDate) => {
  const diffMs = Date.now() - fromDate.getTime()
  if (!Number.isFinite(diffMs) || diffMs < 0) return 'a moment'

  const totalMinutes = Math.floor(diffMs / 60000)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} min${minutes === 1 ? '' : 's'}`)

  return parts.join(' ')
}

const formatAbsoluteTimestamp = (date) => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = date.toLocaleString('en-GB', { month: 'short' })
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${day} ${month} ${date.getFullYear()} ${hours}:${minutes}:${seconds}`
}

const normalizeRepairItem = (repair) => {
  if (!repair) return null
  if (typeof repair === 'string') {
    return {
      category: 'Repair',
      servicetype: 'Repair',
      description: repair,
    }
  }

  const category = repair.category || repair.type || repair.name || 'Repair'
  const servicetype =
    repair.servicetype ||
    repair.service_type?.servicetype ||
    repair.service_type?.name ||
    repair.name ||
    category
  const description = repair.description || repair.note || repair.text || ''

  if (!description) return null

  return { category, servicetype, description }
}

const normalizeRepairList = (repairs) => {
  if (Array.isArray(repairs)) {
    return repairs.map(normalizeRepairItem).filter(Boolean)
  }
  const normalizedRepair = normalizeRepairItem(repairs)
  return normalizedRepair ? [normalizedRepair] : []
}

const normalizeAllItems = (items) => {
  if (!Array.isArray(items)) return []

  return items
    .map((item) => {
      const category = String(item?.service_type?.category || item?.category || '').trim().toLowerCase()
      const description = String(
        item?.description ||
        item?.note ||
        item?.text ||
        item?.service_type?.servicetype ||
        item?.service_type?.name ||
        ''
      ).trim()

      if (!category) return null

      return {
        category,
        servicetype: String(item?.service_type?.servicetype || item?.service_type?.name || '').trim(),
        description,
      }
    })
    .filter(Boolean)
}

const resolveEposAssetUrl = (path) => {
  const normalizedPath = String(path || '').trim()

  if (!normalizedPath) return ''
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath

  return `${EPOS_API_BASE}${normalizedPath.startsWith('/') ? '' : '/'}${normalizedPath}`
}

const normalizeStainMarker = (marker, index) => {
  const markerData = marker?.data || marker || {}
  const x = Number(markerData.x)
  const y = Number(markerData.y)

  if (!Number.isFinite(x) || !Number.isFinite(y)) return null

  return {
    id: marker?.id || `marker-${index}`,
    x,
    y,
  }
}

const normalizeStainLayouts = (items) => {
  if (!Array.isArray(items)) return []

  return items.flatMap((item, itemIndex) => {
    const category = String(item?.service_type?.category || item?.category || '').trim().toLowerCase()
    if (category !== 'stain') return []

    const locations = Array.isArray(item?.locations) ? item.locations : []

    return locations
      .map((location, locationIndex) => {
        const imagePath = location?.layout_details?.image || location?.image || ''
        const imageUrl = resolveEposAssetUrl(imagePath)

        if (!imageUrl) return null

        const markersSource = Array.isArray(location?.markers)
          ? location.markers
          : Array.isArray(item?.markers)
            ? item.markers
            : []

        return {
          id: location?.id || `${itemIndex}-${locationIndex}-${imagePath}`,
          name: location?.layout_details?.name || location?.name || `Location ${locationIndex + 1}`,
          imageUrl,
          markers: markersSource.map(normalizeStainMarker).filter(Boolean),
        }
      })
      .filter(Boolean)
  })
}

const normalizeStainDescriptions = (items) => {
  if (!Array.isArray(items)) return []

  return items
    .filter((item) => String(item?.service_type?.category || item?.category || '').trim().toLowerCase() === 'stain')
    .map((item) => String(item?.description || item?.note || item?.text || '').trim())
    .filter(Boolean)
}

const markerCoordinateToPercent = (value) => {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) return 0

  return Math.max(0, Math.min(100, numericValue / 10))
}

const getStainMarkerStyle = (_layoutId, marker) => {
  const leftPercent = markerCoordinateToPercent(marker?.x)
  const topPercent = markerCoordinateToPercent(marker?.y)

  return {
    left: `${leftPercent}%`,
    top: `${topPercent}%`,
  }
}

const dedupeRepairs = (repairs) => {
  const seen = new Set()

  return repairs.filter((repair) => {
    const key = `${String(repair?.category || '').trim().toLowerCase()}::${String(repair?.description || '').trim().toLowerCase()}`
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const normalizeQcHistoryEntry = (entry) => {
  if (!entry) return null

  const user = entry.user || {}

  return {
    timestamp: formatDateTimeEN(entry.timestamp || entry.created_at || entry.date || ''),
    status: 'Failed',
    reason: entry.reason || entry.action || '',
    user: user.username || user.user_label || user.name || '',
  }
}

const normalizeQcHistory = (qcHistory) => {
  if (!Array.isArray(qcHistory)) return []
  return qcHistory.map(normalizeQcHistoryEntry).filter(Boolean)
}

const normalizeQcNoteEntry = (entry) => {
  if (!entry) return null

  const user = entry.user || {}

  return {
    text: entry.ordernote || entry.note || entry.text || '',
    timestamp: formatDateYMD(entry.timeadded || entry.timestamp || entry.created_at || ''),
    user: user.username || user.user_label || user.name || '',
  }
}

const normalizeQcNotes = (qcNotes) => {
  if (!Array.isArray(qcNotes)) return []
  return qcNotes.map(normalizeQcNoteEntry).filter((entry) => entry && entry.text)
}

const normalizeResponseLabels = (items) => {
  if (!Array.isArray(items)) return []

  return items
    .map((item) => String(item?.label || item?.name || item || '').trim().toLowerCase())
    .filter(Boolean)
}

const PRESS_ONLY_SERVICE_TYPE_IDS = new Set([75, 76, 77])

const GARMENT_CASES = [
  {
    key: 'new-assignment',
    matches: (garment) => Boolean(garment?.pieceUp?.isFirstAssignment),
  },
  {
    key: 'only-garment-in-ticket',
    matches: (garment) => Boolean(garment?.pieceUp?.isFirstAssignment && garment?.pieceUp?.willComplete),
  },
  {
    key: 'completes-ticket',
    matches: (garment) => Boolean(garment?.pieceUp?.willComplete && !garment?.pieceUp?.isFirstAssignment),
  },
  {
    key: 'qc-note',
    matches: (garment) => Array.isArray(garment?.qcNotes) && garment.qcNotes.length > 0,
    apply: (garment) => {
      qcNoteModal.value = { notes: garment.qcNotes }
    },
  },
  {
    key: 'repairs',
    matches: (garment) => Array.isArray(garment?.repairs) && garment.repairs.length > 0,
    apply: (garment) => {
      repairsModal.value = {
        garment,
        repairs: garment.repairs,
        answers: garment.repairs.map(() => undefined),
      }
    },
  },
  {
    key: 'stain-check',
    matches: (garment) => Boolean(garment?.stainCheckRequired),
    apply: (garment) => {
      stainModal.value = { garment }
    },
  },
]

const openGarmentCase = (caseKey, garment) => {
  const caseConfig = GARMENT_CASES.find((candidate) => candidate.key === caseKey)
  if (!caseConfig || typeof caseConfig.apply !== 'function' || !caseConfig.matches(garment)) return

  caseConfig.apply(garment)
}

const openNextGarmentCase = (garment = selectedGarment.value) => {
  const nextCaseKey = pendingGarmentCaseQueue.value.shift()

  if (!nextCaseKey || !garment) return

  openGarmentCase(nextCaseKey, garment)
}

const applyGarmentCases = (garment) => {
  const matchingCaseKeys = GARMENT_CASES
    .filter((caseConfig) => typeof caseConfig.apply === 'function' && caseConfig.matches(garment))
    .map((caseConfig) => caseConfig.key)

  pendingGarmentCaseQueue.value = matchingCaseKeys.slice(1)

  if (matchingCaseKeys[0]) {
    openGarmentCase(matchingCaseKeys[0], garment)
  }
}


// Pattern validators
const patterns = {
  garmentId: /^[gG]\d{8}$/, // G followed by 8 digits
  garmentTag: /^[gG]\d{8}$/, // G followed by 8 digits
  itemId: /^[iI]\d{9}$/, // I followed by 9 digits
  serviceTicket: /^[sS][tT]\d+\/[0-9a-fA-F]+$/, // ST001530438/8f84ec02
  hex24: /^[0-9a-fA-F]{24}$/, // 24 character hex
  qcFail: /^QCS(20|21|22|23|24)$/i, // QCS20–QCS24
  qcReturnOnly: QC_RETURN_ONLY_CODE, // QCS30
  qcPass: /^QCS10$/i, // QC pass
  slotId: /^PUR\d{4}$/i, // Slot barcode format e.g. PUR0101, PUR0202, PUR0901, PUR1121
}

const isValidScan = (value) => {
  const v = (value || '').trim()
  return v && (
    patterns.garmentId.test(v) ||
    patterns.garmentTag.test(v) ||
    patterns.itemId.test(v) ||
    patterns.serviceTicket.test(v) ||
    patterns.hex24.test(v) ||
    patterns.qcFail.test(v) ||
    patterns.qcReturnOnly.test(v) ||
    patterns.qcPass.test(v) ||
    patterns.slotId.test(v)
  )
}

const isVariableLengthScan = (value) => patterns.serviceTicket.test((value || '').trim())

// Debounce scanner input before processing
const SCAN_DEBOUNCE_MS = 120
let scanDebounceTimer = null
let activeGarmentLoadRequestId = 0
let selectedGarmentScanValue = ''

const queueScanProcessing = (delayMs = 0) => {
  if (scanDebounceTimer) clearTimeout(scanDebounceTimer)
  scanDebounceTimer = setTimeout(async () => {
    scanDebounceTimer = null
    if (!isActive.value) return
    const current = (scanInput.value || '').trim()
    if (!current || !isValidScan(current)) return
    await processScan(current)
    if (!isActive.value) return
    scanInput.value = ''
  }, delayMs)
}

watch(scanInput, (newVal) => {
  if (!isActive.value) return
  const input = (newVal || '').trim()
  if (!input) {
    if (scanDebounceTimer) clearTimeout(scanDebounceTimer)
    scanDebounceTimer = null
    return
  }
  if (!isValidScan(input)) return

  if (isVariableLengthScan(input)) {
    queueScanProcessing(SCAN_DEBOUNCE_MS)
    return
  }

  queueScanProcessing(0)
})

const normalizeGarmentDetails = (payload) => {
  const garment = payload?.garment || payload || {}
  const order = payload?.order || garment.order || {}
  const orderItem = payload?.order_item || garment.order_item || {}
  const allItemsSource = payload?.all_items || orderItem?.all_items || garment?.all_items
  const customer = order?.customer || payload?.customer || {}
  const customerTypeLabels = normalizeResponseLabels(customer?.customer_types)
  const customerStateLabels = normalizeResponseLabels(customer?.customer_states)
  const itemService = orderItem?.item_service || payload?.item_service || garment?.item_service || {}
  const serviceTypeId = Number(itemService?.service_type_id ?? itemService?.servicetype_id)
  const pieceUp = payload?.piece_up || orderItem?.piece_up || garment?.piece_up || {}
  const allItems = normalizeAllItems(allItemsSource)
  const stainLayouts = normalizeStainLayouts(allItemsSource)
  const stainDescriptions = normalizeStainDescriptions(allItemsSource)
  const qcHistory = normalizeQcHistory(
    payload?.qc_history ||
    orderItem?.qc_history ||
    garment?.qc_history
  )
  const repairSource =
    payload?.repairs ||
    payload?.repair_list ||
    orderItem?.repairs ||
    orderItem?.repair_list ||
    garment?.repairs ||
    garment?.repair_list ||
    garment?.repair
  const qcNotes = normalizeQcNotes(
    payload?.qc_notes ||
    orderItem?.qc_notes ||
    garment?.qc_notes
  )
  const repairItemsFromAllItems = allItems
    .filter((item) => item.category === 'repair' && item.description)
    .map((item) => ({
      category: 'Repair',
      servicetype: item.servicetype || 'Repair',
      description: item.description,
    }))
  const stainCheckRequired =
    payload?.stain_check_required ??
    payload?.requires_stain_check ??
    payload?.needs_stain_check ??
    orderItem?.stain_check_required ??
    orderItem?.requires_stain_check ??
    orderItem?.needs_stain_check ??
    garment?.stain_check_required ??
    garment?.requires_stain_check ??
    garment?.needs_stain_check ??
    payload?.stain_check ??
    orderItem?.stain_check ??
    garment?.stain_check ??
    allItems.some((item) => item.category === 'stain')

  const repairs = dedupeRepairs([
    ...normalizeRepairList(repairSource),
    ...repairItemsFromAllItems,
  ])

  return {
    id: garment.garment_id || garment.id || '',
    orderItemId: orderItem.id || orderItem.orderitem_id || garment.order_item_id || '',
    orderId: order.id || order.order_id || garment.order_id || '',
    customerName:
      order.customer_name ||
      garment.customer_name ||
      payload?.customer_name ||
      [customer.first_name, customer.last_name].filter(Boolean).join(' '),
    garmentType:
      orderItem?.garment_type?.garment_type ||
      garment.garment_type ||
      garment.type ||
      '',
    dueDate: formatDateEN(order.date_due || order.datedue || garment.date_due || ''),
    timesFailed: qcHistory.length,
    serviceTicket:
      orderItem?.service_ticket?.serviceticket_id ||
      orderItem?.service_ticket_id ||
      garment.service_ticket ||
      '',
    flags: {
      vipCustomer: customerTypeLabels.includes('vip customer') || Boolean(order.vip_customer || garment.vip_customer),
      specialAttention: customerTypeLabels.includes('special attention customer') || Boolean(order.special_attention || garment.special_attention),
      pressOnly: PRESS_ONLY_SERVICE_TYPE_IDS.has(serviceTypeId) || Boolean(orderItem.press_only || garment.press_only),
      newCustomer: customerStateLabels.includes('new signup') || Boolean(order.new_customer || garment.new_customer),
      express: toBoolean(order.next_day ?? garment.next_day ?? garment.express),
    },
    qcHistory,
    pieceUp: {
      isFirstAssignment: toBoolean(pieceUp.is_first_assignment ?? pieceUp.isFirstAssignment),
      willComplete: toBoolean(pieceUp.will_complete ?? pieceUp.willComplete),
      alreadyPiecedUp: toBoolean(pieceUp.already_pieced_up ?? pieceUp.alreadyPiecedUp),
      piecedUpAt: pieceUp.pieced_up_at ?? pieceUp.piecedUpAt ?? null,
    },
    qcNotes,
    repairs,
    stainCheckRequired: toBoolean(stainCheckRequired),
    stainDescriptions,
    stainLayouts,
  }
}

const getGarmentLoadErrorMessage = (error) => {
  const errorCode = error?.response?.data?.code
  const apiMessage = error?.response?.data?.message

  if (errorCode === 'GARMENT_NOT_IN_ORDER') {
    return apiMessage || 'This garment is not in the order for this ticket'
  }

  if (errorCode === 'GARMENT_NOT_FOUND' || errorCode === 'GARMENT_LOAD_ERROR') {
    return apiMessage || 'Unable to find this garment'
  }

  if (errorCode === 'ORDER_COMPLETED') {
    return apiMessage || 'This order is already completed'
  }

  return apiMessage || 'Unable to load garment details'
}

const loadGarmentByScan = async (scannedValue, qcHistory = false, qcNotes = true, surfaceErrors = true) => {
  const upperValue = scannedValue.toUpperCase()
  const logscan = qcNotes && !qcHistory

  const normalizeResponse = (response) => {
    const garmentDetails = response?.data?.data
    return garmentDetails ? normalizeGarmentDetails(garmentDetails) : null
  }

  try {
    if (patterns.itemId.test(scannedValue)) {
      // Remove the leading I and any leading zeros before calling the service.
      const trimmedValue = upperValue.replace(/^I0*/, '')
      return normalizeResponse(await OrderService.getOrderItemDetails(trimmedValue, 'qpup', qcHistory, qcNotes, logscan))
    }
    if (patterns.garmentTag.test(scannedValue)) {
      return normalizeResponse(await OrderService.getGarmentDetailsByTag(upperValue, 'qpup', qcHistory, qcNotes, logscan))
    }
    if (patterns.garmentId.test(scannedValue)) {
      return normalizeResponse(await OrderService.getGarmentDetails(upperValue, 'qpup', qcHistory, qcNotes, logscan))
    }
    if (patterns.hex24.test(scannedValue)) {
      try {
        return normalizeResponse(await OrderService.getGarmentRFIDDetails(upperValue, 'qpup', qcHistory, qcNotes, logscan))
      } catch (error) {
        const errorCode = error?.response?.data?.code
        if (errorCode !== 'GARMENT_LOAD_ERROR') throw error

        return normalizeResponse(await OrderService.getTagRFIDDetails(upperValue, 'qpup', qcHistory, qcNotes, logscan))
      }
    }
    console.log('No OrderService scan handler matched for value:', scannedValue)
  } catch (error) {
    console.error('Error fetching garment details:', error)

    if (surfaceErrors) {
      return {
        loadFailed: true,
        loadErrorMessage: getGarmentLoadErrorMessage(error),
      }
    }
  }

  return null
}

const isSameGarmentRecord = (candidate, garment) => {
  if (!candidate || !garment) return false

  return candidate.id === garment.id && candidate.orderItemId === garment.orderItemId
}

const mergeHydratedQcHistory = (garment, hydratedGarment) => {
  if (!garment || !hydratedGarment) return garment

  return {
    ...garment,
    qcHistory: hydratedGarment.qcHistory,
    timesFailed: hydratedGarment.timesFailed,
  }
}

const hydrateGarmentQcHistory = async (scannedValue, garment, requestId) => {
  const hydratedGarment = await loadGarmentByScan(scannedValue, true, false, false)

  if (!hydratedGarment || !isActive.value || requestId !== activeGarmentLoadRequestId) return

  const currentGarment = selectedGarment.value
  if (isSameGarmentRecord(currentGarment, garment)) {
    selectedGarment.value = mergeHydratedQcHistory(currentGarment, hydratedGarment)
  }

  if (isSameGarmentRecord(stainModal.value?.garment, garment)) {
    stainModal.value = {
      ...stainModal.value,
      garment: mergeHydratedQcHistory(stainModal.value.garment, hydratedGarment),
    }
  }

  if (isSameGarmentRecord(repairsModal.value?.garment, garment)) {
    repairsModal.value = {
      ...repairsModal.value,
      garment: mergeHydratedQcHistory(repairsModal.value.garment, hydratedGarment),
    }
  }

  if (isSameGarmentRecord(qcFailState.value?.garment, garment)) {
    qcFailState.value = {
      ...qcFailState.value,
      garment: mergeHydratedQcHistory(qcFailState.value.garment, hydratedGarment),
    }
  }
}

const preventKeyboard = (event) => {
  if (event.target) {
    event.target.setAttribute('readonly', 'readonly')
    setTimeout(() => {
      event.target.removeAttribute('readonly')
    }, 100)
  }
}

const focusScanInput = async () => {
  await nextTick()

  if (qcNoteModal.value || repairsModal.value || stainModal.value || repeatQcFailModal.value) return

  scanInputElement.value?.focus()
}

const showFailedGarmentState = (garment, reason, infoText = 'Ready to scan garment', isError = false) => {
  selectedGarment.value = null
  qcPassState.value = null
  qcFailState.value = { garment, reason }
  mainInfoIsError.value = isError
  mainInfoText.value = infoText
}

const resetQpupState = async () => {
  selectedGarment.value = null
  qcPassState.value = null
  qcFailState.value = null
  piecedUpState.value = null
  ticketCompleteState.value = null
  pendingGarmentCaseQueue.value = []
  selectedGarmentScanValue = ''
  mainInfoIsError.value = false
  mainInfoText.value = 'Ready to scan garment'
  scanInput.value = ''
  await focusScanInput()
}

const buildAlreadyPiecedUpModalState = async (garment) => {
  const rawTimestamp = garment?.pieceUp?.piecedUpAt
  if (!rawTimestamp) return null

  const parsedDate = new Date(rawTimestamp)
  if (Number.isNaN(parsedDate.getTime())) return null

  if (Date.now() - parsedDate.getTime() > ALREADY_PIECED_UP_WARNING_WINDOW_MS) {
    return null
  }

  const onlyGarmentInTicket = Boolean(garment?.pieceUp?.isFirstAssignment && garment?.pieceUp?.willComplete)
  let slotLabel = ''

  if (!onlyGarmentInTicket && garment?.serviceTicket && garment?.orderItemId) {
    try {
      const slotDetails = normalizeSlotDetails(
        await ServiceTicketService.getServiceTicketSlot(
          garment.serviceTicket,
          false,
          false,
          true,
          garment.orderItemId,
          false
        )
      )
      slotLabel = slotDetails?.slotLabel || slotDetails?.slotId || ''
    } catch (error) {
      console.error('Error fetching service ticket slot for already pieced-up warning:', error)
    }
  }

  return {
    garment,
    relativeText: formatRelativeDuration(parsedDate),
    absoluteText: formatAbsoluteTimestamp(parsedDate),
    onlyGarmentInTicket,
    slotLabel,
  }
}

const openRepeatQcFailPrompt = (garment, config) => {
  repeatQcFailModal.value = {
    garment,
    config,
    timesFailed: Number(garment?.timesFailed || 0),
  }
}

async function answerAlreadyPiecedUp(shouldProceed) {
  const garment = alreadyPiecedUpModal.value?.garment || null
  alreadyPiecedUpModal.value = null

  if (!shouldProceed) {
    await resetQpupState()
    return
  }

  if (garment) {
    await startPieceUpForGarment(garment)
  }
  await focusScanInput()
}

const recordGarmentQcFail = async (garment, failType) => {
  const orderItemId = garment?.orderItemId

  if (!orderItemId) {
    mainInfoIsError.value = true
    mainInfoText.value = 'Unable to fail QC for this garment'
    return false
  }

  try {
    await OrderService.failQCGarment(orderItemId, failType, false, true, true)
    return true
  } catch (error) {
    console.error('Error failing garment QC:', error)
    mainInfoIsError.value = true
    mainInfoText.value = 'Unable to fail QC for this garment'
    return false
  }
}

const buildPieceUpState = async (garment, options = {}) => {
  const serviceTicketId = garment.serviceTicket
  const orderItemId = garment.orderItemId
  const qcReturnOnly = Boolean(options.qcReturnOnly)
  const slotLogscan = options.slotLogscan !== false
  debugServiceTicketId('piece-up:derived-from-garment', serviceTicketId, {
    garmentId: garment.id,
    orderId: garment.orderId,
    qcReturnOnly,
    slotLogscan,
  })

  let slotDetails = null

  if (serviceTicketId) {
    try {
      debugServiceTicketId('piece-up:fetch-slot', serviceTicketId)
      slotDetails = normalizeSlotDetails(await ServiceTicketService.getServiceTicketSlot(serviceTicketId, true, slotLogscan, true, orderItemId, qcReturnOnly))
      if (slotDetails?.isNewAssignment) {
        debugServiceTicketId('piece-up:print-new-assignment', serviceTicketId, {
          orderId: garment.orderId,
        })
        await printServiceTicketQpup(garment.orderId, serviceTicketId)
      }
    } catch (error) {
      console.error('Error fetching service ticket slot:', error)
    }
  } else {
    debugServiceTicketId('piece-up:missing-service-ticket', serviceTicketId, {
      garmentId: garment.id,
      orderId: garment.orderId,
    })
  }

  const isNewAssignment = Boolean(slotDetails?.isNewAssignment)
  const onlyGarmentInTicket = Boolean(slotDetails?.isOnlyGarment)
  const completesTicket = Boolean(slotDetails?.willComplete)
  const awaitingSlotScan = isNewAssignment && !onlyGarmentInTicket
  const slotId = slotDetails?.slotId || ''
  const slotLabel = slotDetails?.slotLabel || ''

  if (!onlyGarmentInTicket && !slotId) {
    return {
      ok: false,
      message: 'Unable to load slot for this ticket',
    }
  }

  const expectedServiceTicket = normalizeServiceTicketBarcode(
    serviceTicketId || `ST${String(garment.orderId).padStart(9, '0')}/00000000`
  )
  debugServiceTicketId('piece-up:expected-service-ticket', expectedServiceTicket, {
    source: serviceTicketId ? 'garment.serviceTicket' : 'fallback-order-derived',
  })

  return {
    ok: true,
    state: {
      garment,
      slotId,
      slotLabel,
      expectedServiceTicket,
      awaitingSlotScan,
      isNewAssignment,
      onlyGarmentInTicket,
      completesTicket,
    },
    message: awaitingSlotScan ? `Scan the slot ${slotLabel}` : 'Scan the white ticket to confirm',
  }
}

const startPieceUpForGarment = async (garment, options = {}) => {
  const pieceUpState = await buildPieceUpState(garment, options)

  if (!pieceUpState.ok) {
    mainInfoIsError.value = true
    mainInfoText.value = pieceUpState.message
    return false
  }

  mainInfoIsError.value = false
  piecedUpState.value = null
  ticketCompleteState.value = null
  qcPassState.value = pieceUpState.state
  selectedGarment.value = null
  qcFailState.value = null
  mainInfoText.value = pieceUpState.message
  return true
}

const refreshSelectedGarmentQcHistory = async (garment) => {
  if (!selectedGarmentScanValue) return garment

  const hydratedGarment = await loadGarmentByScan(selectedGarmentScanValue, true, false, false)
  if (!hydratedGarment || !isSameGarmentRecord(hydratedGarment, garment)) {
    return garment
  }

  const mergedGarment = mergeHydratedQcHistory(garment, hydratedGarment)
  if (isSameGarmentRecord(selectedGarment.value, garment)) {
    selectedGarment.value = mergedGarment
  }

  return mergedGarment
}

async function processScan(input) {
  if (!isActive.value) return
  if (repeatQcFailModal.value) {
    mainInfoIsError.value = true
    mainInfoText.value = 'Respond to the QC fail prompt'
    return
  }
  if (ticketCompleteState.value && !ticketCompleteState.value.cleared) {
    mainInfoIsError.value = true
    mainInfoText.value = 'Click Clear Slot to continue'
    return
  }
  const inputUpper = input.toUpperCase()

  if (selectedGarment.value) {
    const isQcPass = patterns.qcPass.test(input)
    const isQcReturnOnly = patterns.qcReturnOnly.test(input)
    const isQcFail = /^QCS(20|21|22|23|24)$/i.test(inputUpper)
    if (!isQcPass && !isQcReturnOnly && !isQcFail) {
      mainInfoIsError.value = true
      mainInfoText.value = 'Pass or fail QC now'
      return
    }
  }

  if (!selectedGarment.value && !qcPassState.value) {
    const isSupportedLookupScan =
      patterns.garmentId.test(input) ||
      patterns.itemId.test(input) ||
      patterns.hex24.test(input) ||
      patterns.serviceTicket.test(input)
    if (!isSupportedLookupScan) {
      mainInfoIsError.value = true
      mainInfoText.value = 'Ready to scan garment'
      return
    }
  }

  if (qcPassState.value?.awaitingSlotScan) {
    if (patterns.slotId.test(input)) {
      const scannedSlotId = inputUpper.trim()
      if (scannedSlotId === qcPassState.value.slotId) {
        mainInfoIsError.value = false
        qcPassState.value = { ...qcPassState.value, awaitingSlotScan: false }
        mainInfoText.value = 'Scan the white ticket'
      } else {
        mainInfoIsError.value = true
        mainInfoText.value = `Scan the slot ${qcPassState.value.slotLabel}`
      }
    } else {
      mainInfoIsError.value = true
      mainInfoText.value = `Scan the slot ${qcPassState.value.slotLabel}`
    }
    return
  }

  if (qcPassState.value && !qcPassState.value.awaitingSlotScan) {
    const promptText = 'Scan the white ticket to confirm'
    if (patterns.serviceTicket.test(input)) {
      const scannedST = normalizeServiceTicketBarcode(input)
      let pieceUpResult = null
      try {
        pieceUpResult = normalizePieceUpResult(
          await RFIDService.startPieceUp(scannedST, [qcPassState.value.garment.orderItemId].filter(Boolean), true)
        )
      } catch (error) {
        console.error('Error starting piece up:', error)
        mainInfoIsError.value = true
        mainInfoText.value = error?.response?.data?.message || promptText
        return
      }

      const ticketComplete = Boolean(pieceUpResult?.ticketComplete || qcPassState.value.completesTicket)

      mainInfoIsError.value = false
      if (qcPassState.value.onlyGarmentInTicket) {
        piecedUpState.value = { singleGarmentTicketComplete: true }
        mainInfoText.value = 'Ready to scan garment'
      } else if (ticketComplete) {
        ticketCompleteState.value = {
          slotLabel: qcPassState.value.slotLabel,
          serviceTicketId: qcPassState.value.expectedServiceTicket,
          cleared: false,
        }
        mainInfoText.value = 'Click Clear Slot to continue'
      } else {
        piecedUpState.value = { slotLabel: qcPassState.value.slotLabel }
        mainInfoText.value = 'Ready to scan garment'
      }
      qcPassState.value = null
    } else {
      mainInfoIsError.value = true
      mainInfoText.value = promptText
    }
    return
  }

  if (patterns.qcPass.test(input) && selectedGarment.value) {
    const garment = selectedGarment.value
    if (garment?.pieceUp?.alreadyPiecedUp) {
      const modalState = await buildAlreadyPiecedUpModalState(garment)
      if (modalState) {
        alreadyPiecedUpModal.value = modalState
        return
      }
    }
    await startPieceUpForGarment(garment)
    return
  }

  if (patterns.qcReturnOnly.test(input) && selectedGarment.value) {
    const garment = selectedGarment.value
    const orderItemId = garment?.orderItemId

    if (!orderItemId) {
      mainInfoIsError.value = true
      mainInfoText.value = 'Unable to return this garment to piece up'
      return
    }

    try {
      await OrderService.returnOnlyQCGarment(orderItemId, true, true)
    } catch (error) {
      console.error('Error marking garment as QC return only:', error)
      mainInfoIsError.value = true
      mainInfoText.value = error?.response?.data?.message || 'Unable to return this garment to piece up'
      return
    }

    await startPieceUpForGarment(garment, { qcReturnOnly: true })
    return
  }

  const qcMatch = inputUpper.match(/^QCS(20|21|22|23|24)$/i)
  if (qcMatch && selectedGarment.value) {
    const code = 'QCS' + qcMatch[1]
    const config = QC_FAIL_CODES[code]
    let garment = selectedGarment.value
    const orderItemId = garment.orderItemId

    if (!orderItemId) {
      mainInfoIsError.value = true
      mainInfoText.value = 'Unable to fail QC for this garment'
      return
    }

    if (Number(garment.timesFailed) <= 0) {
      garment = await refreshSelectedGarmentQcHistory(garment)
      if (!isActive.value) return
    }

      if (Number(garment.timesFailed) > 0) {
        openRepeatQcFailPrompt(garment, config)
      return
    }

    const failLogged = await recordGarmentQcFail(garment, config.failType)
    if (!failLogged) return

    showFailedGarmentState(garment, config.reason)
    return
  }

  const garmentLoadRequestId = ++activeGarmentLoadRequestId
  const garment = await loadGarmentByScan(input, false, true)
  if (!isActive.value) return
  if (garment?.loadFailed) {
    mainInfoIsError.value = true
    mainInfoText.value = garment.loadErrorMessage
    selectedGarment.value = null
    qcFailState.value = null
    qcPassState.value = null
    piecedUpState.value = null
    ticketCompleteState.value = null
  } else if (garment) {
    mainInfoIsError.value = false
    qcFailState.value = null
    qcPassState.value = null
    piecedUpState.value = null
    ticketCompleteState.value = null
    selectedGarmentScanValue = input
    selectedGarment.value = garment
    mainInfoText.value = 'Pass or fail QC now'
    applyGarmentCases(garment)
    hydrateGarmentQcHistory(input, garment, garmentLoadRequestId)
  } else {
    mainInfoIsError.value = false
    selectedGarment.value = null
    qcFailState.value = null
    qcPassState.value = null
    piecedUpState.value = null
    ticketCompleteState.value = null
    selectedGarmentScanValue = ''
    mainInfoText.value = 'Ready to scan garment'
  }
}

async function clearSlot() {
  if (ticketCompleteState.value && !ticketCompleteState.value.cleared) {
    const serviceTicketId = ticketCompleteState.value.serviceTicketId
    debugServiceTicketId('clear-slot:read-from-ticket-complete-state', serviceTicketId, {
      slotLabel: ticketCompleteState.value.slotLabel,
    })

    if (!serviceTicketId) {
      debugServiceTicketId('clear-slot:missing-service-ticket', serviceTicketId, {
        slotLabel: ticketCompleteState.value.slotLabel,
      })
      mainInfoIsError.value = true
      mainInfoText.value = 'Unable to clear slot for this ticket'
      return
    }

    try {
      debugServiceTicketId('clear-slot:request', serviceTicketId)
      await ServiceTicketService.clearServiceTicketSlot(serviceTicketId)
    } catch (error) {
      console.error('Error clearing service ticket slot:', error)
      mainInfoIsError.value = true
      mainInfoText.value = 'Unable to clear slot for this ticket'
      return
    }

    mainInfoIsError.value = false
    ticketCompleteState.value = { ...ticketCompleteState.value, cleared: true }
    mainInfoText.value = 'Ready to scan garment'
    await focusScanInput()
  }
}

async function reprintCurrentServiceTicket() {
  const orderId = qcPassState.value?.garment?.orderId
  const serviceTicketId = qcPassState.value?.expectedServiceTicket

  if (!orderId || !serviceTicketId || isReprintingServiceTicket.value) {
    return
  }

  isReprintingServiceTicket.value = true

  try {
    await OrderService.printServiceTicketsQpup(orderId, {
      tickets: [serviceTicketId],
    })
    reprintFeedbackMessage.value = 'Service ticket reprinted'
    if (reprintFeedbackTimer) {
      clearTimeout(reprintFeedbackTimer)
    }
    reprintFeedbackTimer = setTimeout(() => {
      reprintFeedbackMessage.value = ''
      reprintFeedbackTimer = null
    }, 2500)
    await focusScanInput()
  } catch (error) {
    console.error('Error reprinting service ticket:', error)
    mainInfoIsError.value = true
    mainInfoText.value = error?.response?.data?.message || 'Unable to reprint service ticket'
  } finally {
    isReprintingServiceTicket.value = false
  }
}

async function closeQcNoteModal() {
  qcNoteModal.value = null
  openNextGarmentCase()
  await focusScanInput()
}

const failGarmentQcFromModal = async (garment, failType, reason) => {
  let currentGarment = garment

  if (Number(currentGarment?.timesFailed) <= 0) {
    currentGarment = await refreshSelectedGarmentQcHistory(currentGarment)
    if (!isActive.value) return false
  }

  if (Number(currentGarment?.timesFailed) > 0) {
    openRepeatQcFailPrompt(currentGarment, { failType, reason })
    return true
  }

  const qcFailed = await recordGarmentQcFail(currentGarment, failType)
  if (!qcFailed) return false

  showFailedGarmentState(currentGarment, reason)
  return true
}

async function answerRepeatQcFail(shouldProceedToPieceUp) {
  if (!repeatQcFailModal.value) return

  const { garment, config } = repeatQcFailModal.value
  repeatQcFailModal.value = null

  const failLogged = await recordGarmentQcFail(garment, config.failType)
  if (!failLogged) {
    await focusScanInput()
    return
  }

  if (shouldProceedToPieceUp) {
    const pieceUpStarted = await startPieceUpForGarment(garment, { slotLogscan: false })
    if (!pieceUpStarted) {
      showFailedGarmentState(garment, config.reason, 'Unable to load slot for this ticket', true)
    }
  } else {
    showFailedGarmentState(garment, config.reason)
  }

  await focusScanInput()
}

async function answerRepair(index, yes) {
  if (!repairsModal.value) return
  if (!yes) {
    const garment = repairsModal.value.garment
    repairsModal.value = null
    await failGarmentQcFromModal(garment, 'repair', 'Repair')
    await focusScanInput()
    return
  }
  repairsModal.value.answers[index] = true
  const allYes = repairsModal.value.answers.every(a => a === true)
  if (allYes) {
    repairsModal.value = null
    openNextGarmentCase()
    await focusScanInput()
  }
}

async function answerStain(yes) {
  if (!stainModal.value) return
  const garment = stainModal.value.garment
  stainModal.value = null
  if (!yes) {
    await failGarmentQcFromModal(garment, 'stain', 'Stain Check')
    await focusScanInput()
    return
  }

  openNextGarmentCase(garment)
  await focusScanInput()
}

async function answerStainRecleaned() {
  if (!stainModal.value) return
  const garment = stainModal.value.garment
  stainModal.value = null
  openNextGarmentCase(garment)
  await focusScanInput()
}


</script>

<style scoped>
.app-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Centered content column on desktop */
.app-inner {
  min-height: 100vh;
}
#scan-input {
      margin-top: 20px;
}

@media (min-width: 768px) {
  .app-inner {
    max-width: 700px;
    margin: 0 auto;
  }
}

.title-bar {
  background-color: #001e44;
  color: white;
  padding: 0.5rem 0;
  text-align: center;
  width: 100%;
}

.title-text {
  margin: 0;
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
}

.main-info-text {
  margin: 0.5rem 0 0 0;
  padding: 0;
  font-size: 0.9rem;
  font-weight: bold;
  color: #198754;
  text-align: left;
}

.main-info-text.main-info-text--error {
  color: #dc3545 !important;
}

.blade-container {
  width: 100%;
  padding: 0;
  background-color: #f8f9fa;
  border-radius: 0;
}

.input-content {
  padding: 1.5rem;
  padding-top: 1rem;
  padding-bottom: 0;
}

.form-control-lg {
  font-size: 1.1rem;
  padding: 0.75rem 1rem;
}

/* Desktop typography and spacing */
@media (min-width: 768px) {
  .title-text {
    font-size: 1.5rem;
  }
  
  .main-info-text {
    font-size: 0.95rem;
  }
  
  .input-content {
    padding: 2rem;
    padding-top: 1.25rem;
    padding-bottom: 0;
  }
}

@media (max-width: 767px) {
  .input-content {
    padding: 1rem;
    padding-bottom: 0;
  }
}

@media (min-width: 768px) {
  .blade-container {
    max-width: 700px;
    margin: 0 auto;
  }
}

/* QC fail page background */
.app-inner.qc-fail-background {
  background-color: #fff9e6;
}

.qc-fail-area {
  background-color: #fff3b0;
  padding: 1.5rem;
  padding-top: 1rem;
  min-height: 12rem;
}

.qc-fail-message {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: bold;
  color: #dc3545;
  text-align: center;
}

.qc-fail-inner {
  max-width: 560px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 0.5rem;
}

@media (min-width: 768px) {
  .qc-fail-area {
    padding: 2rem;
  }
  .qc-fail-inner {
    max-width: 560px;
  }
}

/* QC Pass slot assignment */
.slot-assignment {
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #e0e0e0;
}

.new-assignment-heading {
  background-color: #ffffff;
  color: #1a3389;
  font-weight: bold;
  font-size: 1.4rem;
  text-align: center;
  padding: 1rem;
  border: 2px solid #4285f4;
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
}

.completes-ticket-heading {
  background-color: #e8f5e9;
  color: #1b5e20;
  font-weight: bold;
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  border: 2px solid #4caf50;
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
}

.single-garment-instruction {
  background-color: #f3e8ff;
  border: 1px solid #d4b5ff;
  border-radius: 0.5rem;
  color: #5b2a86;
  font-weight: 600;
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin-bottom: 1.25rem;
}

.slot-box {
  background-color: #fff3b0;
  border: 1px solid #f0d24d;
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;
  margin-bottom: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  min-width: 7rem;
  margin-left: auto;
  margin-right: auto;
}

.slot-instruction-bubble {
  background-color: #fff3b0;
  color: #5c4700;
  font-weight: 600;
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  border: 1px solid #f0d24d;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
}

.slot-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #5c4700;
  text-align: center;
}

.slot-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 0.95rem;
}

.slot-info-label {
  color: #6c757d;
}

.slot-info-value {
  font-weight: 500;
  color: #1a1a1a;
}

.reprint-feedback-message {
  margin: 1rem 0 0;
  text-align: center;
  color: #198754;
  font-weight: 700;
  font-size: 0.95rem;
}

.reprint-ticket-btn {
  display: block;
  width: min(50%, 18rem);
  min-width: 12rem;
  margin: 1.25rem auto 0;
  padding: 0.85rem 1.25rem;
  background-color: #1a3389;
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid #10256a;
  border-radius: 0.5rem;
  cursor: pointer;
  box-shadow: 0 0.35rem 0.9rem rgba(26, 51, 137, 0.18);
  transition: background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.reprint-ticket-btn:hover:not(:disabled) {
  background-color: #2447b8;
  transform: translateY(-1px);
  box-shadow: 0 0.45rem 1rem rgba(26, 51, 137, 0.24);
}

.reprint-ticket-btn:disabled {
  opacity: 0.7;
  cursor: wait;
  transform: none;
  box-shadow: 0 0.25rem 0.6rem rgba(26, 51, 137, 0.12);
}

@media (max-width: 767px) {
  .reprint-ticket-btn {
    width: min(60%, 16rem);
  }
}

@media (min-width: 768px) {
  .slot-assignment {
    padding: 1.25rem 2rem 2rem;
  }
}

/* Pieced up / ticket complete */
.pieced-up-area {
  padding: 1rem;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
}

.pieced-up-text {
  margin: 0;
  padding: 1rem;
  background-color: #f8f9fa;
  color: #198754;
  font-weight: bold;
  font-size: 1.25rem;
  text-align: center;
}

.ticket-complete-message {
  margin: 0.5rem 0 1rem 0;
  text-align: center;
}

.ticket-complete-message .ticket-complete-line {
  margin: 0.25rem 0;
  font-size: 1.125rem;
  color: #333;
  text-align: center;
}

.clear-slot-btn {
  display: block;
  margin: 0 auto;
  padding: 0.75rem 2rem;
  background-color: #001e44;
  color: white;
  font-weight: bold;
  font-size: 1rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
}

.clear-slot-btn:hover {
  background-color: #002a5c;
}

@media (min-width: 768px) {
  .pieced-up-text {
    font-size: 1.5rem;
  }
}

/* QC Note modal */
.qc-note-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem 1rem 1rem;
}

.qc-note-modal-box {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  min-width: 320px;
  max-width: 90vw;
  display: flex;
  flex-direction: column;
}

.qc-note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
  flex-shrink: 0;
}

.qc-note-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
}

.qc-note-content {
  padding: 1rem 1.25rem;
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.qc-note-content.qc-note-content--scrollable {
  max-height: 8rem;
  overflow-y: auto;
}

.qc-note-entry + .qc-note-entry {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.qc-note-entry-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.qc-note-entry-meta {
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.qc-note-footer {
  padding: 1rem 1.25rem 1.25rem;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
}

.qc-note-close-btn {
  padding: 0.6rem 2rem;
  background-color: #001e44;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
}

.qc-note-close-btn:hover {
  background-color: #002a5c;
}

/* Modal enter/leave transition */
.qc-note-modal-enter-active,
.qc-note-modal-leave-active {
  transition: opacity 0.25s ease;
}

.qc-note-modal-enter-active .qc-note-modal-box,
.qc-note-modal-leave-active .qc-note-modal-box {
  transition: transform 0.25s ease;
}

.qc-note-modal-enter-from,
.qc-note-modal-leave-to {
  opacity: 0;
}

.qc-note-modal-enter-from .qc-note-modal-box,
.qc-note-modal-leave-to .qc-note-modal-box {
  transform: scale(0.95);
}

.qc-note-modal-enter-to .qc-note-modal-box,
.qc-note-modal-leave-from .qc-note-modal-box {
  transform: scale(1);
}

/* Repairs confirmation modal */
.repairs-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem 1rem 1rem;
}

.repairs-modal-box {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  min-width: 320px;
  max-width: 90vw;
  padding: 1.25rem 1.5rem 1.5rem;
}

.repairs-header {
  margin-bottom: 1.25rem;
}

.repairs-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
}

.repairs-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.repair-item {
  padding: 1rem 1.25rem;
  border: 1px solid #e8e8e8;
  border-radius: 0.35rem;
  background: #fafafa;
}

.repair-category {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.35rem;
}

.repair-description {
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
}

.repair-actions {
  display: flex;
  gap: 0.75rem;
}

.repair-btn {
  flex: 1;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 0.35rem;
  cursor: pointer;
  color: white;
}

.repair-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.repair-btn--yes {
  background-color: #198754;
}

.repair-btn--yes:hover:not(:disabled) {
  background-color: #157347;
}

.repair-btn--no {
  background-color: #dc3545;
}

.repair-btn--no:hover:not(:disabled) {
  background-color: #bb2d3b;
}

.repairs-overlay.qc-note-modal-enter-active,
.repairs-overlay.qc-note-modal-leave-active {
  transition: opacity 0.25s ease;
}

.repairs-overlay.qc-note-modal-enter-active .repairs-modal-box,
.repairs-overlay.qc-note-modal-leave-active .repairs-modal-box {
  transition: transform 0.25s ease;
}

.repairs-overlay.qc-note-modal-enter-from,
.repairs-overlay.qc-note-modal-leave-to {
  opacity: 0;
}

.repairs-overlay.qc-note-modal-enter-from .repairs-modal-box,
.repairs-overlay.qc-note-modal-leave-to .repairs-modal-box {
  transform: scale(0.95);
}

.repairs-overlay.qc-note-modal-enter-to .repairs-modal-box,
.repairs-overlay.qc-note-modal-leave-from .repairs-modal-box {
  transform: scale(1);
}

/* Stain Removed modal */
.stain-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem 1rem 1rem;
  overflow: hidden;
  overscroll-behavior: contain;
}

.stain-modal-box {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  min-width: 320px;
  width: min(560px, 90vw);
  max-width: 90vw;
  max-height: calc(100vh - 2rem);
  padding: 1.25rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
}

.stain-header {
  margin-bottom: 1rem;
  width: 100%;
  flex-shrink: 0;
}

.stain-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.stain-times-failed {
  margin: 0.35rem 0 0 0;
  font-size: 0.9rem;
  color: #6c757d;
}

.stain-layouts-wrap {
  width: 100%;
  margin-bottom: 1.25rem;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-right: 0.25rem;
}

.stain-layouts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  width: 100%;
}

.stain-layout-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  max-width: 220px;
  margin: 0 auto;
}

.stain-layout-canvas {
  position: relative;
  width: 100%;
}

.stain-modal-image {
  width: 100%;
  height: auto;
  display: block;
  max-width: 220px;
  margin: 0 auto;
}

.stain-marker {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background-color: #ff9f1c;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 2px rgba(0, 30, 68, 0.16);
  transform: translate(-50%, -50%);
}

.stain-layout-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
}

.stain-layouts-empty {
  width: 100%;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.35rem;
  color: #6c757d;
  text-align: center;
  flex: 1 1 auto;
}

.stain-actions {
  display: flex;
  gap: 0.75rem;
  width: 100%;
  justify-content: center;
  flex-shrink: 0;
}

.stain-btn {
  padding: 0.5rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.35rem;
  cursor: pointer;
  min-width: 80px;
}

.stain-btn--yes {
  background-color: #001e44;
  color: white;
  border: none;
}

.stain-btn--yes:hover {
  background-color: #002a5c;
}

.stain-btn--no {
  background-color: #fff;
  color: #1a1a1a;
  border: 2px solid #001e44;
}

.stain-btn--no:hover {
  background-color: #f5f5f5;
}

.stain-btn--recleaned {
  margin-top: 0.75rem;
  width: 100%;
  background-color: #ffc107;
  color: #1a1a1a;
  border: 1px solid #e0a800;
  flex-shrink: 0;
}

.stain-btn--recleaned:hover {
  background-color: #ffca2c;
}

.stain-overlay.qc-note-modal-enter-active,
.stain-overlay.qc-note-modal-leave-active {
  transition: opacity 0.25s ease;
}

.stain-overlay.qc-note-modal-enter-active .stain-modal-box,
.stain-overlay.qc-note-modal-leave-active .stain-modal-box {
  transition: transform 0.25s ease;
}

.stain-overlay.qc-note-modal-enter-from,
.stain-overlay.qc-note-modal-leave-to {
  opacity: 0;
}

.stain-overlay.qc-note-modal-enter-from .stain-modal-box,
.stain-overlay.qc-note-modal-leave-to .stain-modal-box {
  transform: scale(0.95);
}

.stain-overlay.qc-note-modal-enter-to .stain-modal-box,
.stain-overlay.qc-note-modal-leave-from .stain-modal-box {
  transform: scale(1);
}

/* Repeat QC fail confirmation modal */
.repeat-qc-fail-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.repeat-qc-fail-modal-box {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: min(420px, 92vw);
  padding: 1.25rem 1.5rem 1.5rem;
}

.repeat-qc-fail-header {
  margin-bottom: 0.75rem;
}

.repeat-qc-fail-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a1a;
}

.repeat-qc-fail-message {
  margin: 0;
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
}

.already-pieced-up-badge {
  display: inline-block;
  margin: 0 0.25rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background-color: #dc3545;
  color: #fff;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
}

.already-pieced-up-timestamp {
  display: inline-block;
  margin-left: 0.25rem;
  color: #6c757d;
  font-size: 0.95rem;
}

.repeat-qc-fail-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.repeat-qc-fail-btn {
  flex: 1;
  padding: 0.65rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.35rem;
  cursor: pointer;
}

.repeat-qc-fail-btn--yes {
  background-color: #001e44;
  color: #fff;
  border: none;
}

.repeat-qc-fail-btn--yes:hover {
  background-color: #002a5c;
}

.repeat-qc-fail-btn--proceed {
  background-color: #198754;
  color: #fff;
  border: none;
}

.repeat-qc-fail-btn--proceed:hover {
  background-color: #157347;
}

.repeat-qc-fail-btn--no {
  background-color: #fff;
  color: #1a1a1a;
  border: 1px solid #ced4da;
}

.repeat-qc-fail-btn--no:hover {
  background-color: #f8f9fa;
}

@media (max-width: 767px) {
  .stain-modal-box {
    padding: 1rem;
  }

  .stain-layouts-wrap {
    max-height: 44vh;
  }

  .stain-layouts-grid {
    grid-template-columns: 1fr;
  }

  .stain-layout-card,
  .stain-modal-image {
    max-width: 180px;
  }
}
</style>
